import React, { useState, useEffect } from "react";
import {
    TextField,
    Button,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    CircularProgress,
    Snackbar,
    IconButton,
    Tooltip
} from "@mui/material";
import { FaPlus } from "react-icons/fa";
import { BsPencilFill, BsTrashFill } from "react-icons/bs";
import Loader from '../../../Loader';
import Header from '../../Component/Header';
import axios from "../../../Axiosinstance";
import DeleteIcon from '@mui/icons-material/Delete'

const Sales = () => {
    const [openModal, setOpenModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [salesperson, setSalesperson] = useState({ name: "", email: [""], phone: [""], telephone: [""] });
    const [salespersons, setSalespersons] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [salespersonToDelete, setSalespersonToDelete] = useState(null);
    const [openConfirmDelete, setOpenConfirmDelete] = useState(false);


    const isFormValid = () => {
        const { name, email, phone, telephone } = salesperson;
    
        // Check if name is non-empty
        const isNameValid = name && name.trim() !== "";
    
        // Check if emails are valid
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const isEmailsValid = email && email.every((e) => e.trim() !== "" && emailRegex.test(e));
    
        // Check if phone numbers are valid
        const phoneRegex = /^[689]\d{7}$/;
        const isPhonesValid = phone && phone.every((p) => phoneRegex.test(p));
    
        // Check if telephone numbers are valid
        const telephoneRegex = /^6\d{7}$/;
        const isTelephonesValid = telephone && telephone.every((t) => telephoneRegex.test(t));
    
        return isNameValid && isEmailsValid && isPhonesValid && isTelephonesValid;
    };
    
    

    useEffect(() => {
        fetchSalespersons();
    }, []);

    const fetchSalespersons = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/wsq/sales-person');
            setSalespersons(response.data);
        } catch (error) {
            console.error("Error fetching salespersons", error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (sp = { name: "", email: [""], phone: [""], telephone: [""] }) => {
        setOpenModal(true);
        setError("");
        setSalesperson({
            ...sp, 
            email: sp.email || [""], // Ensure email is always an array
            phone: sp.phone || [""],  // Ensure phone is always an array
            telephone: sp.telephone || [""] // Ensure telephone is always an array
        });
    };
    

    const handleCloseModal = () => setOpenModal(false);

    const handleAddField = (field) => {
        setSalesperson((prev) => ({
            ...prev,
            [field]: [...prev[field], ""],
        }));
    };

    const handleRemoveField = (field, index) => {
        setSalesperson((prev) => ({
            ...prev,
            [field]: prev[field].filter((_, i) => i !== index),
        }));
    };

    const handleFieldChange = (field, index, value) => {
        setSalesperson((prev) => ({
            ...prev,
            [field]: prev[field].map((item, i) => (i === index ? value : item)),
        }));
    };

    const handleAddOrUpdateSalesperson = async () => {
        if (!isFormValid()) {
            setError("Please fill all fields correctly before saving.");
            return;
        }
    
        setSaving(true);
        try {
            const payload = {
                name: salesperson.name.trim(),
                email: salesperson.email.filter(Boolean),
                phone: salesperson.phone.filter(Boolean),
                telephone: salesperson.telephone.filter(Boolean),
            };
    
            if (salesperson.id) {
                await axios.put(`/wsq/sales-person/${salesperson.id}`, payload);
                setToastMessage("Salesperson updated successfully");
            } else {
                await axios.post('/wsq/sales-person', payload);
                setToastMessage("Salesperson added successfully");
            }
            setSaving(false);
            setOpenModal(false);
            fetchSalespersons();
            setToastOpen(true);
        } catch (error) {
            setError("Failed to save salesperson");
            console.error(error);
            setSaving(false);
        }
    };
    
    
    const handleShowDeleteConfirm = (id) => {
        setSalespersonToDelete(id);
        setOpenConfirmDelete(true);
    };

    const handleDeleteSalesperson = async () => {
        try {
            await axios.delete(`/wsq/sales-person/${salespersonToDelete}`);
            setToastMessage("Salesperson deleted successfully");
            fetchSalespersons();  // Refresh the list after deletion
            setToastOpen(true);
        } catch (error) {
            setError("Failed to delete salesperson");
            console.log(error);
        } finally {
            setOpenConfirmDelete(false);
            setSalespersonToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setOpenConfirmDelete(false);
        setSalespersonToDelete(null);
    };

    const handleCloseToast = () => setToastOpen(false);

    return (
        <div style={{ marginTop: '24px', maxWidth: '1000px', margin: 'auto', padding: '16px' }}>
            <Header />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Salesperson Directory
            </Typography>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px', alignItems: 'center' }}>
                <Button
                    variant="contained"
                    color="primary"
                    sx={{
                        backgroundColor: '#1976d2',
                        '&:hover': { backgroundColor: '#115293' },
                        padding: '10px 20px',
                        fontWeight: 'bold',
                        borderRadius: '8px',
                    }}
                    onClick={handleOpenModal}
                    startIcon={<FaPlus />}
                >
                    New Salesperson
                </Button>
            </div>

            {loading ? (
                <Loader />
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Phone</TableCell>
                                <TableCell>Telephone</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {salespersons.length > 0 ? (
                                salespersons.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((sp, index) => (
                                    <TableRow key={sp.id}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>{sp.name}</TableCell>
                                        <TableCell>{(sp.email || []).join(", ")}</TableCell>
                                        <TableCell>{(sp.phone || []).join(", ")}</TableCell>
                                        <TableCell>{(sp.telephone || []).join(", ")}</TableCell>
                                        <TableCell>
                                            <Tooltip title="Edit">
                                                <IconButton color="primary" onClick={() => handleOpenModal(sp)}>
                                                    <BsPencilFill />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton color="secondary" onClick={() => handleShowDeleteConfirm(sp.id)}>
                                                    <BsTrashFill />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        No salespersons found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <TablePagination
                component="div"
                count={salespersons.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                rowsPerPageOptions={[10, 25, 50]}
            />

            <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="sm">
                <DialogTitle>{salesperson.id ? "Edit Salesperson" : "Add Salesperson"}</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Name"
                        name="name"
                        value={salesperson.name}
                        onChange={(e) => setSalesperson({ ...salesperson, name: e.target.value })}
                        fullWidth
                        variant="outlined"
                        margin="dense"
                    />

                    {salesperson.email.map((email, index) => (
                        <div key={index} style={{ display: 'flex', alignItems: 'center' }}>
                            <TextField
                                label={`Email ${index + 1}`}
                                value={email}
                                onChange={(e) => handleFieldChange("email", index, e.target.value)}
                                fullWidth
                                variant="outlined"
                                margin="dense"
                                type="email"
                            />
                            <IconButton style={{
        backgroundColor: '#f8d7da',
        borderRadius: '50%',
        padding: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        marginRight: '-5px',
    }} onClick={() => handleRemoveField("email", index)}><DeleteIcon /></IconButton>
                        </div>
                    ))}
                    <Button variant="outlined" onClick={() => handleAddField("email")}>Add Email</Button>

                    {salesperson.phone.map((phone, index) => (
  <div key={index} style={{ display: 'flex', alignItems: 'center' }}>
    <TextField
      label={`Phone ${index + 1}`}
      placeholder="6/8/9/XXXXXX" // Example format
      value={phone}
      onChange={(e) => {
        const value = e.target.value;

        // Only allow values that start with 6, 8, or 9 and are 8 digits long
        if (/^[689]\d{0,7}$/.test(value) || value === "") {
          handleFieldChange("phone", index, value);
        }
      }}
      onInput={(e) => {
        // Limit the input length to 8 digits
        if (e.target.value.length > 8) {
          e.target.value = e.target.value.slice(0, 8);
        }
      }}
      fullWidth
      variant="outlined"
      margin="dense"
      type="tel"
    />
    <IconButton
      style={{
        backgroundColor: '#f8d7da',
        borderRadius: '50%',
        padding: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        marginRight: '-5px',
      }}
      onClick={() => handleRemoveField("phone", index)}
    >
      <DeleteIcon />
    </IconButton>
  </div>
))}
<Button variant="outlined" onClick={() => handleAddField("phone")}>
  Add Phone
</Button>


                    {salesperson.telephone.map((telephone, index) => (
                        <div key={index} style={{ display: 'flex', alignItems: 'center' }}>
                            <TextField
                                label={`Telephone ${index + 1}`}
                                  placeholder="6XXXXXXX"
                                value={telephone}
                                onChange={(e) => {
                                    const value = e.target.value;
                    
                                    // Allow only numbers, limit to 8 digits, and ensure it starts with '6'
                                    if (/^6\d{0,7}$/.test(value) || value === "") {
                                        handleFieldChange("telephone", index, value);
                                    }
                                }}
                                onInput={(e) => {
                                    // Prevent input from exceeding 8 characters
                                    if (e.target.value.length > 8) {
                                        e.target.value = e.target.value.slice(0, 8);
                                    }
                                }}
                                fullWidth
                                variant="outlined"
                                margin="dense"
                                type="tel"
                            />
                            <IconButton style={{
                                backgroundColor: '#f8d7da',
                                borderRadius: '50%',
                                padding: '8px',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                marginRight: '-5px',
                            }} onClick={() => handleRemoveField("telephone", index)}> <DeleteIcon /></IconButton>
                        </div>
                    ))}
                    <Button variant="outlined" onClick={() => handleAddField("telephone")}>Add Telephone</Button>
                     

                    {error && <Typography color="error">{error}</Typography>}
                </DialogContent>
                <DialogActions>
    <Button variant="contained" onClick={handleCloseModal} color="error">
        Cancel
    </Button>
    <Button 
    variant="contained" 
    onClick={handleAddOrUpdateSalesperson} 
    color="primary" 
    disabled={!isFormValid() || saving}
>
    {saving ? <CircularProgress size={24} /> : 'Save'}
</Button>

</DialogActions>
            </Dialog>

             {/* Confirmation Modal */}
             <Dialog
                open={openConfirmDelete}
                onClose={handleCancelDelete}
            >
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this salesperson? This action cannot be undone.
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" color='error' onClick={handleCancelDelete} >Cancel</Button>
                    <Button variant="contained" onClick={handleDeleteSalesperson} color="primary">Delete</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={toastOpen}
                autoHideDuration={6000}
                onClose={handleCloseToast}
                message={toastMessage}
            />
        </div>
    );
};

export default Sales;
